/**
 * Organised PDP: ease gallery frame height when active media aspect ratio changes.
 * Frame width always fills the media column; only height is animated.
 * Patches media-gallery#setActiveMedia (prototype) and rebinds thumbnail clicks
 * so already-constructed galleries pick up the animated path.
 *
 * Overflow stays visible for the whole transition. Using overflow:hidden clipped
 * Dawn's media-list shadow/focus padding and changed margin collapse vs the
 * thumbnail row, which caused a ~20px snap at the end of every resize.
 */
(function () {
  const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');
  const DURATION_MS = 560;
  const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
  let protoPatched = false;

  /**
   * @param {HTMLElement} viewer
   */
  function clearInline(viewer) {
    viewer.style.height = '';
    viewer.style.transition = '';
    viewer.classList.remove('gma-organised-media-viewer--animating');
  }

  /**
   * @param {HTMLElement} viewer
   * @returns {number}
   */
  function measureHeight(viewer) {
    const previousHeight = viewer.style.height;
    const previousTransition = viewer.style.transition;

    viewer.style.transition = 'none';
    viewer.style.height = 'auto';
    const height = Math.round(viewer.getBoundingClientRect().height);

    viewer.style.height = previousHeight;
    viewer.style.transition = previousTransition;

    return height;
  }

  /**
   * @param {HTMLElement} gallery
   * @param {() => void} applyChange
   */
  function animateSwitch(gallery, applyChange) {
    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    if (!(viewer instanceof HTMLElement) || REDUCE.matches) {
      applyChange();
      return;
    }

    window.clearTimeout(gallery._gmaMediaAnimTimer);

    const fromHeight = measureHeight(viewer) || Math.round(viewer.getBoundingClientRect().height);
    if (fromHeight <= 0) {
      applyChange();
      return;
    }

    const run = (gallery._gmaMediaAnimToken = (gallery._gmaMediaAnimToken || 0) + 1);

    viewer.classList.add('gma-organised-media-viewer--animating');
    viewer.style.height = `${fromHeight}px`;
    viewer.style.transition = 'none';

    applyChange();

    const toHeight = measureHeight(viewer);

    if (toHeight <= 0) {
      clearInline(viewer);
      return;
    }

    if (Math.abs(toHeight - fromHeight) <= 1) {
      clearInline(viewer);
      return;
    }

    void viewer.offsetHeight;
    viewer.style.transition = `height ${DURATION_MS}ms ${EASE}`;

    requestAnimationFrame(() => {
      if (run !== gallery._gmaMediaAnimToken) return;
      viewer.style.height = `${toHeight}px`;
    });

    const finish = () => {
      if (run !== gallery._gmaMediaAnimToken) return;
      clearInline(viewer);
    };

    const onEnd = (event) => {
      if (event.target !== viewer || event.propertyName !== 'height') return;
      viewer.removeEventListener('transitionend', onEnd);
      finish();
    };

    viewer.addEventListener('transitionend', onEnd);
    gallery._gmaMediaAnimTimer = window.setTimeout(() => {
      viewer.removeEventListener('transitionend', onEnd);
      finish();
    }, DURATION_MS + 80);
  }

  function patchPrototype() {
    if (protoPatched) return;
    const Ctor = customElements.get('media-gallery');
    if (!Ctor || !Ctor.prototype?.setActiveMedia) return;

    const original = Ctor.prototype.setActiveMedia;
    Ctor.prototype.setActiveMedia = function setActiveMediaOrganised(mediaId, prepend) {
      if (!this.closest?.('.product--organised')) {
        return original.call(this, mediaId, prepend);
      }

      animateSwitch(this, () => original.call(this, mediaId, prepend));
    };

    protoPatched = true;
  }

  /**
   * Rebind thumbnail buttons so they call the (possibly patched) setActiveMedia.
   * Dawn binds the method in the constructor before our patch can run.
   * @param {HTMLElement} gallery
   */
  function rebindThumbnails(gallery) {
    if (gallery.dataset.gmaMediaThumbsRebound === 'true') return;
    const thumbs = gallery.querySelector('[id^="GalleryThumbnails"]');
    if (!thumbs) return;

    thumbs.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
      const button = mediaToSwitch.querySelector('button');
      if (!button) return;

      const next = button.cloneNode(true);
      button.replaceWith(next);
      next.addEventListener('click', () => {
        gallery.setActiveMedia(mediaToSwitch.dataset.target, false);
      });
    });

    gallery.dataset.gmaMediaThumbsRebound = 'true';
  }

  /**
   * @param {HTMLElement} gallery
   */
  function enhance(gallery) {
    if (!(gallery instanceof HTMLElement)) return;
    if (!gallery.closest('.product--organised')) return;

    patchPrototype();
    rebindThumbnails(gallery);
    gallery.dataset.gmaMediaTransition = 'true';
  }

  function init() {
    document.querySelectorAll('.product--organised media-gallery').forEach(enhance);
  }

  function boot() {
    customElements.whenDefined('media-gallery').then(() => {
      patchPrototype();
      init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', () => {
    boot();
  });
})();
